import React, { PropsWithChildren, useEffect, useRef } from 'react';

import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useAuthContext } from 'state/auth';
import { ModalAuthTrigger } from 'state/auth/types';

const AuthRequired = ({ children }: PropsWithChildren) => {
  const { isAuthenticated, isReAuthenticating } = useAuthContext();

  useAuthRedirects({ skip: isReAuthenticating });

  return isAuthenticated || isReAuthenticating ? <>{children}</> : null;
};

export default AuthRequired;

/**
 * HOC that requires a user to be signed in before rendering the passed
 * in <Component />. If a user is not signed in it redirects to /signup.
 */
export const withAuthRequired = (Component: React.ComponentType) => (props: any) => {
  return (
    <AuthRequired>
      <Component {...props} />
    </AuthRequired>
  );
};

export const useAuthRequiredInModal = (skip = false) => {
  const { isAuthenticated, loading: isLoadingAuth, modalAuthIsOpen } = useAuthContext();
  const { openSignInModal } = useAuthContext();

  const didDisplay = useRef(skip); // only display it once so a user can get out if they decide not to auth
  useEffect(() => {
    if (!isAuthenticated && !isLoadingAuth && !modalAuthIsOpen && !didDisplay.current) {
      didDisplay.current = true;
      openSignInModal({ trigger: ModalAuthTrigger.Modal });
    }
  }, [isAuthenticated, isLoadingAuth, modalAuthIsOpen, openSignInModal]);
};
